import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchCredits, fetchPacks, checkoutPack, fetchPaymentStatus } from "../../services/creditsService";
import { fmtCredits, videosFor, imagesFor } from "../../utils/credits";
import { yieldText, plural } from "../../utils/pricingLogic";

const REASON = {
  "generate:image": "Génération image", "generate:video": "Génération vidéo",
  "generate:batch": "Lot créatif", topup: "Recharge", signup_bonus: "Bonus de bienvenue",
};
const label = (r) => REASON[r] || (r || "").replace(/^pack:/, "Pack ").replace(/^generate:/, "Génération ");

// Grille de secours si l'API ne renvoie pas `usage` (ancien serveur).
const FALLBACK_USAGE = [
  { key: "video", label: "Génération vidéo", credits: 1, note: "1 vidéo = 1 crédit" },
  { key: "image", label: "Génération image", credits: 0.1, note: "10 images = 1 crédit" },
  { key: "ad_creation", label: "Création de publicité", credits: 0, note: "Gratuit — illimité" },
  { key: "ab_test", label: "Split testing (A/B)", credits: 0, note: "Gratuit — illimité" },
];

const Credits = () => {
  const [data, setData] = useState(null);
  const [packs, setPacks] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p, w] = await Promise.all([
        fetchCredits(), fetchPacks().catch(() => ({ packs: [] })), fetchPaymentStatus().catch(() => null),
      ]);
      setData(c); setPacks(p?.packs || []); setWallet(w);
    } catch (e) { toast.error(e?.response?.data?.message || "Chargement impossible."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const buy = async (pack) => {
    setBuying(pack.id);
    try {
      const res = await checkoutPack(pack.id);
      if (res?.url) window.location.href = res.url;
      else toast.warn("Paiement non configuré (Stripe).");
    } catch (e) {
      toast.error(e?.response?.status === 501
        ? "Paiement non configuré côté serveur (Stripe)."
        : (e?.response?.data?.message || "Achat impossible."));
    } finally { setBuying(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-24 text-gray-400"><div className="w-8 h-8 border-4 border-[#221C51]/20 border-t-[#221C51] rounded-full animate-spin" /></div>;

  const balance = Number(data?.balance ?? 0);
  const usage = data?.usage?.length ? data.usage : FALLBACK_USAGE;
  const paid = usage.filter((u) => u.credits > 0);
  const free = usage.filter((u) => !u.credits);

  return (
    <div className="p-2 max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#221C51]">Crédits &amp; Paiement</h2>
        <p className="text-sm text-gray-400 mt-0.5">Vous ne payez que la génération créative. Le média-buying est gratuit.</p>
      </div>

      {/* Solde */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <p className="text-[11px] uppercase font-bold text-gray-400">⚡ Crédits IA</p>
          <p className="text-3xl font-bold text-[#221C51] mt-1 tabular-nums">{fmtCredits(balance)}</p>
          <p className="text-xs text-gray-500 mt-1">
            soit <b>{plural(data?.remaining?.videos ?? videosFor(balance), "vidéo")}</b> ou <b>{plural(data?.remaining?.images ?? imagesFor(balance), "image")}</b>
          </p>
          {balance < 1 && <p className="text-xs text-amber-600 font-semibold mt-2">Solde bas — rechargez pour générer des vidéos.</p>}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <p className="text-[11px] uppercase font-bold text-gray-400">💰 Wallet</p>
          <p className="text-3xl font-bold text-green-600 mt-1">{Number(wallet?.wallet_balance ?? wallet?.balance ?? 0).toFixed(2)} €</p>
          <p className="text-xs text-gray-500 mt-1">Budget publicitaire (dépensé sur les régies).</p>
        </div>
      </div>

      {/* Grille tarifaire à l'usage */}
      <h3 className="font-bold text-[#221C51] mb-3">Ce que coûte chaque action</h3>
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100">
          {paid.map((u) => (
            <div key={u.key} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-[#221C51]">{u.label}</p>
                <p className="text-xs text-gray-400">{u.note}</p>
              </div>
              <span className="text-sm font-bold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-full whitespace-nowrap">
                {fmtCredits(u.credits)} cr
              </span>
            </div>
          ))}
        </div>
        <div className="bg-green-50/50 rounded-2xl border border-green-100 divide-y divide-green-100">
          {free.map((u) => (
            <div key={u.key} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-[#221C51]">{u.label}</p>
                <p className="text-xs text-gray-400">{u.note}</p>
              </div>
              <span className="text-sm font-bold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">Gratuit</span>
            </div>
          ))}
        </div>
      </div>

      {/* Packs */}
      <h3 className="font-bold text-[#221C51] mb-1">Recharger des crédits</h3>
      <p className="text-xs text-gray-400 mb-3">Plus le pack est grand, moins le crédit coûte cher.</p>
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        {packs.map((p) => (
          <div key={p.id} className={`bg-white rounded-2xl border shadow-sm p-4 flex items-center justify-between ${p.best ? "border-[#221C51] ring-1 ring-[#221C51]/20" : "border-gray-100"}`}>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-[#221C51]">{p.label}</span>
                {p.bonus > 0 && <span className="text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5 font-semibold">+{fmtCredits(p.bonus)} bonus</span>}
                {p.best && <span className="text-xs bg-[#221C51] text-white rounded-full px-2 py-0.5 font-semibold">Meilleure offre</span>}
              </div>
              <p className="text-sm text-gray-600 mt-1 font-medium">{fmtCredits(p.total)} crédits</p>
              <p className="text-xs text-gray-400">
                {yieldText(p.yield)}
                {p.pricePerCredit ? ` · ${p.pricePerCredit} €/crédit` : ""}
              </p>
            </div>
            <button onClick={() => buy(p)} disabled={buying === p.id}
              className="px-5 py-2 bg-[#221C51] text-white font-bold rounded-xl hover:bg-[#2e265e] disabled:opacity-50 whitespace-nowrap">
              {buying === p.id ? "…" : `${p.priceEur} €`}
            </button>
          </div>
        ))}
      </div>

      {/* Journal */}
      <h3 className="font-bold text-[#221C51] mb-3">Mouvements récents</h3>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100">
        {(data?.ledger || []).length === 0 ? (
          <p className="text-sm text-gray-400 p-4">Aucun mouvement pour le moment.</p>
        ) : (data.ledger || []).slice(0, 20).map((m, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
            <span className="text-gray-600">{label(m.reason)}</span>
            <span className="flex items-center gap-3">
              <span className={`font-bold tabular-nums ${m.delta < 0 ? "text-red-600" : "text-green-600"}`}>
                {m.delta > 0 ? "+" : ""}{fmtCredits(m.delta)}
              </span>
              <span className="text-xs text-gray-400 tabular-nums w-16 text-right">{fmtCredits(m.balance_after)}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Credits;
